import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { bildirimAPI, bakisAPI } from '../../api/api';
import './Bildirimler.css';
import KayanSecici from '../Common/KayanSecici';
import BosDurum from '../Common/BosDurum';
import { YENI_TASARIM } from '../../utils/yeniTasarim';

/**
 * Bildirim merkezi — üst çubuktaki zil ikonu.
 *
 * Tek bir yerde toplanır: yaklaşan duruşmalar, bekleyen masraf
 * talepleri, yeni gelen postalar, müvekkilin yüklediği belgeler, süresi
 * dolan vekaletler ve müvekkil hesabındaki hatalı giriş denemeleri.
 *
 * Sunucuda ayrı bir bildirim tablosu tutulmuyor; hepsi zaten
 * sistemdeki veriden türetiliyor. Her bildirimin kararlı bir anahtarı
 * var ve "okundu" bilgisi o anahtara bağlı olarak kullanıcı başına
 * sunucuda saklanıyor.
 *
 * Neden zaman karşılaştırması değil: duruşma ve vekalet bildirimlerinin
 * zamanı GELECEKTE. "Son bakıştan sonra mı" sorusu bunlar için her
 * zaman "evet" çıkıyordu; okundu işaretlense bile listede kalıyorlardı.
 */

export const SON_BAKIS_ANAHTARI = 'bildirim.sonBakis';

const TUR_SIMGE = {
  durusma: '⚖',
  masraf: '₺',
  posta: '✉',
  belge: '📄',
  vekalet: '📋',
  guvenlik: '🔒',
};

const zamanYaz = (d) => {
  if (!d) return '';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '';
  const fark = Math.round((t.getTime() - Date.now()) / 60000);   // dakika
  const mutlak = Math.abs(fark);

  if (mutlak < 60) return fark >= 0 ? `${mutlak} dk sonra` : `${mutlak} dk önce`;
  const saat = Math.round(mutlak / 60);
  if (saat < 24) return fark >= 0 ? `${saat} saat sonra` : `${saat} saat önce`;
  const gun = Math.round(saat / 24);
  if (gun < 30) return fark >= 0 ? `${gun} gün sonra` : `${gun} gün önce`;
  return t.toLocaleDateString('tr-TR');
};

const Bildirimler = () => {
  const navigate = useNavigate();
  const [acik, setAcik] = useState(false);
  const [liste, setListe] = useState([]);
  const [yeniAdet, setYeniAdet] = useState(0);
  const [yukleniyor, setYukleniyor] = useState(false);
  /** 'yeni' | 'okunan' */
  const [sekme, setSekme] = useState('yeni');
  const [sonBakis, setSonBakis] = useState(null);
  /** Panelin ekrandaki yeri — zil düğmesine göre hesaplanır. */
  const [konum, setKonum] = useState(null);
  const kutuRef = useRef(null);
  const zilRef = useRef(null);
  const panelRef = useRef(null);

  const yukle = useCallback(async () => {
    try {
      // Son bakış sunucuda tutuluyor; telefondan girince ya da önbellek
      // temizlenince rozet sıfırlanmasın diye.
      let bakis = null;
      try { bakis = (await bakisAPI.getir())?.bildirim ?? null; } catch { /* yoksay */ }
      setSonBakis(bakis);

      const d = await bildirimAPI.getir(bakis);
      setListe(d?.liste ?? []);
      setYeniAdet(Number(d?.yeniAdet) || 0);
    } catch {
      // Sessiz: bildirim çekilemedi diye üst çubuk bozulmamalı.
      setListe([]);
      setYeniAdet(0);
    }
  }, []);

  useEffect(() => {
    yukle();
    // Uzun açık kalan oturumlarda da güncel kalsın.
    const z = window.setInterval(yukle, 3 * 60 * 1000);
    return () => window.clearInterval(z);
  }, [yukle]);

  // Dışarı tıklayınca kapansın.
  useEffect(() => {
    if (!acik) return undefined;
    const disari = (e) => {
      // Panel artık sayfa kökünde; hem zilin hem panelin dışına
      // tıklanmış olmalı.
      const zilde = kutuRef.current && kutuRef.current.contains(e.target);
      const panelde = panelRef.current && panelRef.current.contains(e.target);
      if (!zilde && !panelde) setAcik(false);
    };
    const esc = (e) => { if (e.key === 'Escape') setAcik(false); };
    document.addEventListener('mousedown', disari);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', disari);
      document.removeEventListener('keydown', esc);
    };
  }, [acik]);

  /** Zilin ekrandaki yerine göre paneli konumlandırır. */
  const konumHesapla = useCallback(() => {
    const el = zilRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const dar = window.innerWidth <= 520;
    if (dar) {
      // Dar ekranda panel tam genişlikte, üstten sabit.
      setKonum({ top: r.bottom + 8, left: 12, right: 12 });
    } else {
      // Sağ kenarı zille hizala, ekrandan taşmasın.
      const sag = Math.max(12, window.innerWidth - r.right);
      setKonum({ top: r.bottom + 10, right: sag });
    }
  }, []);

  useEffect(() => {
    if (!acik) return undefined;
    konumHesapla();
    window.addEventListener('resize', konumHesapla);
    window.addEventListener('scroll', konumHesapla, true);
    return () => {
      window.removeEventListener('resize', konumHesapla);
      window.removeEventListener('scroll', konumHesapla, true);
    };
  }, [acik, konumHesapla]);

  const ac = async () => {
    const yeniDurum = !acik;
    setAcik(yeniDurum);
    if (yeniDurum) {
      konumHesapla();
      setYukleniyor(true);
      await yukle();
      setYukleniyor(false);
    }
  };

  /** "Hepsini okundu say" — sunucu listeyi yeniden türetip işaretler. */
  const hepsiniOkunduYap = async () => {
    // Önce ekranda uygula: ağ yanıtını beklemek gecikme gibi hissettiriyor.
    setListe((eski) => eski.map((b) => ({ ...b, okundu: true })));
    setYeniAdet(0);
    try {
      await bildirimAPI.hepsiOkundu();
      await bakisAPI.isaretle('bildirim').catch(() => {});
    } catch {
      // Yazılamadıysa gerçeği geri getir; sessizce "okundu" görünmesin.
      yukle();
    }
  };

  /** Tek bir bildirimi okundu işaretler. */
  const okunduYap = async (b) => {
    if (!b?.anahtar || b.okundu) return;
    setListe((eski) => eski.map((x) => (x.anahtar === b.anahtar ? { ...x, okundu: true } : x)));
    setYeniAdet((n) => Math.max(0, n - 1));
    try {
      await bildirimAPI.okundu([b.anahtar]);
    } catch {
      yukle();
    }
  };

  const git = (b) => {
    // Tıklanan bildirim okunmuş sayılır; diğerleri olduğu gibi kalır.
    okunduYap(b);
    setAcik(false);
    if (b.yol) navigate(b.yol);
  };

  /* Okundu bilgisi sunucudan geliyor; burada tekrar hesaplanmıyor.
     Bir bildirim ancak açıkça işaretlenirse okunmuş sayılır. */
  const yeniMi = (b) => !(b.okundu ?? b.Okundu ?? false);

  const yeniler = liste.filter(yeniMi);
  const okunanlar = liste.filter((b) => !yeniMi(b));
  const gosterilen = sekme === 'yeni' ? yeniler : okunanlar;

  return (
    <div className="bldr" ref={kutuRef}>
      <button
        type="button"
        ref={zilRef}
        className={'bldr-zil' + (yeniAdet > 0 ? ' dolu' : '')}
        onClick={ac}
        aria-label={yeniAdet > 0 ? `${yeniAdet} yeni bildirim` : 'Bildirimler'}
        aria-expanded={acik}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
             strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {yeniAdet > 0 && (
          <span className="bldr-rozet">{yeniAdet > 99 ? '99+' : yeniAdet}</span>
        )}
      </button>

      {acik && createPortal(
        <div
          className="bldr-panel"
          role="dialog"
          aria-label="Bildirimler"
          ref={panelRef}
          style={konum ? {
            top: konum.top,
            right: konum.right,
            left: konum.left !== undefined ? konum.left : 'auto',
          } : undefined}
        >
          <header className="bldr-panel-basi">
            <h3>Bildirimler</h3>
            {yeniler.length > 0 && (
              <button type="button" className="bldr-okundu" onClick={hepsiniOkunduYap}>
                Hepsini okundu say
              </button>
            )}
          </header>

          {YENI_TASARIM ? (
            <KayanSecici
              className="bldr-ks"
              etiket="Bildirimler"
              secili={sekme}
              onSec={setSekme}
              secenekler={[
                { anahtar: 'yeni', etiket: 'Yeni', adet: yeniler.length },
                { anahtar: 'okunan', etiket: 'Okunanlar', adet: okunanlar.length },
              ]}
            />
          ) : (
          <nav className="bldr-sekmeler">
            <button
              type="button"
              className={'bldr-sekme' + (sekme === 'yeni' ? ' aktif' : '')}
              onClick={() => setSekme('yeni')}
            >
              Yeni
              {yeniler.length > 0 && <span className="bldr-sekme-adet">{yeniler.length}</span>}
            </button>
            <button
              type="button"
              className={'bldr-sekme' + (sekme === 'okunan' ? ' aktif' : '')}
              onClick={() => setSekme('okunan')}
            >
              Okunanlar
              {okunanlar.length > 0 && <span className="bldr-sekme-adet">{okunanlar.length}</span>}
            </button>
          </nav>
          )}

          {yukleniyor ? (
            <p className="bldr-bos">Yükleniyor…</p>
          ) : gosterilen.length === 0 && YENI_TASARIM ? (
            <BosDurum
              simge="bildirim"
              baslik={sekme === 'yeni' ? 'Her şey yolunda' : 'Okunmuş bildirim yok'}
              metin={sekme === 'yeni' ? 'Şu an yeni bildirim yok. Yeni bir gelişme olduğunda burada göreceksiniz.' : 'Okuduğunuz bildirimler burada listelenir.'}
            />
          ) : gosterilen.length === 0 ? (
            <p className="bldr-bos">
              {sekme === 'yeni' ? 'Yeni bildirim yok.' : 'Okunmuş bildirim yok.'}
            </p>
          ) : (
            <ul className="bldr-liste">
              {gosterilen.map((b, i) => (
                <li key={b.anahtar || i} className="bldr-oge">
                  <button
                    type="button"
                    className={'bldr-satir bldr-' + (b.onem || 'bilgi')}
                    onClick={() => git(b)}
                  >
                    <span className="bldr-simge" aria-hidden="true">
                      {TUR_SIMGE[b.tur] || '•'}
                    </span>
                    <span className="bldr-govde">
                      <span className="bldr-baslik">{b.baslik}</span>
                      {b.aciklama && <span className="bldr-aciklama">{b.aciklama}</span>}
                    </span>
                    <span className="bldr-zaman">{zamanYaz(b.zaman)}</span>
                  </button>

                  {/* Satırın kendisi dosyaya götürüyor; okundu işareti
                      ayrı bir düğme olmalı ki bildirimi kapatmak için
                      dosyayı açmak zorunda kalınmasın. */}
                  {sekme === 'yeni' && (
                    <button
                      type="button"
                      className="bldr-tekil-okundu"
                      onClick={() => okunduYap(b)}
                      title="Okundu işaretle"
                      aria-label="Okundu işaretle"
                    >
                      ✓
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Bildirimler;
